<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { loadStripe } from '@stripe/stripe-js'
import type { Stripe, StripeElements } from '@stripe/stripe-js'
import { useAuth } from '@/composables/useAuth'
import { useCreditBalance } from '@/composables/useCreditBalance'
import { useCreditLedger } from '@/composables/useCreditLedger'
import { useLocale } from '@/composables/useLocale'
import { createCreditPurchase } from '@/api/credits'
import { formatCentsAsCurrency } from '@/utils/currencyFormat'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import CreditLedgerTable from '@/components/organizer/newsletter/CreditLedgerTable.vue'

const { t } = useI18n()
const route  = useRoute()
const { user } = useAuth()
const { currentLocale } = useLocale()

const orgId = computed<number | null>(() => {
  const slug = route.params['orgSlug'] as string
  const found = user.value?.organisations.find((o) => o.slug === slug)
  return found?.id ?? null
})

const { balance, loading: balanceLoading, refresh: refreshBalance } = useCreditBalance(orgId.value ?? 0)
const { entries, loading: ledgerLoading, error: ledgerError, meta, page, perPage, fetch: fetchLedger, setPage, setPerPage } = useCreditLedger(orgId.value ?? 0)

// Pre-fill from query param (when coming back from "Buy credits" detour)
const prefillQuantity = computed<number>(() => {
  const q = route.query['prefill']
  if (typeof q === 'string') {
    const n = parseInt(q, 10)
    return isNaN(n) ? 500 : Math.max(1, n)
  }
  return 500
})

// Purchase form state
const PRESET_BUNDLES = [500, 1000, 5000]
const quantity = ref<number>(prefillQuantity.value)
const customQty = ref<string>('')
const useCustom = ref<boolean>(false)

const effectiveQuantity = computed<number>(() => {
  if (useCustom.value) {
    const n = parseInt(customQty.value, 10)
    return isNaN(n) || n < 1 ? 0 : n
  }
  return quantity.value
})

const pricePerCredit = computed<number>(() => balance.value?.price_per_credit ?? 1)
const currency       = computed<string>(() => balance.value?.currency ?? 'eur')

const previewCents = computed<number>(() => effectiveQuantity.value * pricePerCredit.value)

const costPreview = computed<string>(() => {
  const euros = previewCents.value / 100
  return euros.toLocaleString(undefined, { style: 'currency', currency: currency.value.toUpperCase() })
})

// The authoritative amount for the in-flight purchase, as returned by the
// server when the payment intent was created — credit pricing is
// runtime-editable, so this can differ from `previewCents` if it changed
// between the quantity selector and clicking "Buy now" (spec §6.2.2 / §10 Q3).
// Never derived from `costPreview` — it is exactly what Stripe will charge.
const confirmedAmountCents = ref<number | null>(null)

const confirmedAmount = computed<string>(() => {
  if (confirmedAmountCents.value === null) return costPreview.value
  return formatCentsAsCurrency(confirmedAmountCents.value, currency.value, currentLocale.value)
})

const priceChangedSincePreview = computed<boolean>(() => {
  return confirmedAmountCents.value !== null && confirmedAmountCents.value !== previewCents.value
})

// Stripe payment element state
const stripeInstance = ref<Stripe | null>(null)
const stripeElements = ref<StripeElements | null>(null)
const stripeReady    = ref<boolean>(false)
const purchasing     = ref<boolean>(false)
const paymentError   = ref<string | null>(null)
const awaitingWebhook = ref<boolean>(false)
const purchaseSuccess = ref<string | null>(null)

const PAYMENT_ELEMENT_ID = 'credit-payment-element'

async function initPaymentElement(clientSecret: string, publishableKey: string): Promise<void> {
  stripeInstance.value = await loadStripe(publishableKey)
  if (!stripeInstance.value) {
    paymentError.value = t('credits.page.stripe_load_error')
    return
  }
  stripeElements.value = stripeInstance.value.elements({
    clientSecret,
    appearance: {
      theme: 'night',
      variables: {
        colorPrimary: '#34d399',
        colorBackground: '#1e293b',
        colorText: '#f8fafc',
        colorTextSecondary: '#94a3b8',
        colorTextPlaceholder: '#475569',
        colorDanger: '#f87171',
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, sans-serif',
        fontSizeBase: '14px',
        borderRadius: '8px',
        spacingUnit: '4px',
      },
      rules: {
        '.Input': {
          backgroundColor: '#0f172a',
          border: '1px solid #334155',
          boxShadow: 'none',
          color: '#f8fafc',
        },
        '.Input:focus': {
          border: '1px solid #34d399',
          boxShadow: '0 0 0 1px #34d399',
          outline: 'none',
        },
        '.Input--invalid': {
          border: '1px solid #f87171',
          boxShadow: 'none',
        },
        '.Label': {
          color: '#94a3b8',
          fontWeight: '500',
        },
        '.Tab': {
          backgroundColor: '#1e293b',
          border: '1px solid #334155',
          color: '#94a3b8',
        },
        '.Tab:hover': {
          backgroundColor: '#263348',
          color: '#f8fafc',
        },
        '.Tab--selected': {
          backgroundColor: '#34d39915',
          border: '1px solid #34d399',
          color: '#34d399',
        },
        '.TabIcon--selected': {
          fill: '#34d399',
        },
        '.TabLabel--selected': {
          color: '#34d399',
        },
        '.Block': {
          backgroundColor: '#0f172a',
          border: '1px solid #1e293b',
        },
        '.Error': {
          color: '#f87171',
        },
      },
    },
  })
  const el = stripeElements.value.create('payment')
  el.on('ready', () => { stripeReady.value = true })
  el.on('loaderror', (event) => {
    paymentError.value = event.error?.message ?? t('credits.page.stripe_form_load_error')
  })
  await nextTick()
  el.mount(`#${PAYMENT_ELEMENT_ID}`)
}

async function handleBuyNow(): Promise<void> {
  if (!orgId.value || effectiveQuantity.value < 1) return
  purchasing.value = true
  paymentError.value = null
  awaitingWebhook.value = false
  purchaseSuccess.value = null
  confirmedAmountCents.value = null

  try {
    const intent = await createCreditPurchase(orgId.value, effectiveQuantity.value)
    // The authoritative amount to be charged — set before mounting the
    // Payment Element so the payment step never shows the (possibly stale)
    // client-side preview.
    confirmedAmountCents.value = intent.amount
    await initPaymentElement(intent.client_secret, intent.stripe_publishable_key)
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }
    if (err.response?.data?.errors) {
      const first = Object.values(err.response.data.errors)[0]
      paymentError.value = first?.[0] ?? t('credits.page.purchase_failed')
    } else {
      paymentError.value = err.response?.data?.message ?? t('credits.page.purchase_failed')
    }
  } finally {
    purchasing.value = false
  }
}

async function handleConfirmPayment(): Promise<void> {
  if (!stripeInstance.value || !stripeElements.value) return
  purchasing.value = true
  paymentError.value = null

  const returnUrl = `${window.location.origin}/organizer/organisations/${route.params['orgSlug']}/credits?payment=complete`

  const { error } = await stripeInstance.value.confirmPayment({
    elements: stripeElements.value,
    confirmParams: { return_url: returnUrl },
  })

  if (error) {
    paymentError.value = error.message ?? t('credits.page.payment_failed')
    purchasing.value = false
  }
  // On success Stripe redirects — credits land via webhook asynchronously
}

// Handle return from Stripe redirect
const paymentStatus = computed<string | null>(() => {
  const s = route.query['payment']
  return typeof s === 'string' ? s : null
})

watch(paymentStatus, (status) => {
  if (status === 'complete') {
    awaitingWebhook.value = true
    purchaseSuccess.value = t('credits.page.payment_success')
    void refreshBalance()
    void fetchLedger()
  }
}, { immediate: true })

// Pagination
function onPageChange(newPage: number): void {
  // The page watcher below refetches the ledger.
  setPage(newPage)
}

function onPerPageChange(newPerPage: number): void {
  // setPerPage resets the page to 1. When the user was on a later page, the page watcher below
  // refetches; when already on page 1 the watcher does not fire, so fetch explicitly.
  const wasOnFirstPage = page.value === 1
  setPerPage(newPerPage)
  if (wasOnFirstPage) void fetchLedger()
}

function selectPreset(preset: number): void {
  quantity.value = preset
  useCustom.value = false
  customQty.value = ''
}

onMounted(async () => {
  if (!orgId.value) return
  await Promise.all([refreshBalance(), fetchLedger()])

  // Pre-fill quantity from query param
  if (prefillQuantity.value > 0) {
    const isPreset = PRESET_BUNDLES.includes(prefillQuantity.value)
    if (isPreset) {
      quantity.value = prefillQuantity.value
      useCustom.value = false
    } else {
      useCustom.value = true
      customQty.value = String(prefillQuantity.value)
    }
  }
})

watch(page, () => void fetchLedger())
</script>

<template>
  <OrganizerLayout>
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Page heading with balance -->
      <div class="flex items-start justify-between mb-8">
        <div>
          <h1 class="text-3xl font-bold text-white">{{ t('credits.page.heading') }}</h1>
          <p class="mt-1 text-slate-400">{{ t('credits.page.subheading') }}</p>
        </div>
        <div class="text-right">
          <p class="text-xs text-slate-400 mb-0.5">{{ t('credits.page.current_balance_label') }}</p>
          <p class="text-3xl font-bold text-white">
            <span v-if="balanceLoading && !balance" class="text-slate-500">—</span>
            <span v-else>{{ balance?.balance.toLocaleString() ?? '—' }}</span>
          </p>
          <p class="text-xs text-slate-500 mt-0.5">{{ t('credits.page.current_balance_unit') }}</p>
        </div>
      </div>

      <!-- Purchase success message -->
      <div
        v-if="purchaseSuccess"
        class="mb-6 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-5 py-3 text-sm text-emerald-400"
      >
        {{ purchaseSuccess }}
      </div>

      <!-- Buy credits panel -->
      <div class="rounded-xl bg-slate-800/50 border border-slate-700 p-6 mb-8">
        <h2 class="text-base font-semibold text-white mb-4">{{ t('credits.page.buy_credits_heading') }}</h2>

        <!-- Preset bundles -->
        <div class="flex flex-wrap gap-3 mb-4">
          <button
            v-for="preset in PRESET_BUNDLES"
            :key="preset"
            type="button"
            class="px-5 py-2.5 rounded-lg border text-sm font-medium transition-colors"
            :class="!useCustom && quantity === preset
              ? 'border-emerald-500 bg-emerald-500/15 text-emerald-400'
              : 'border-slate-600 text-slate-400 hover:border-slate-500 hover:text-white'"
            @click="selectPreset(preset)"
          >
            {{ preset.toLocaleString() }}
          </button>
          <!-- Custom quantity -->
          <div class="flex items-center gap-2">
            <input
              v-model="customQty"
              type="number"
              min="1"
              :placeholder="t('credits.page.preset_custom_placeholder')"
              class="w-28 bg-slate-800 border rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
              :class="useCustom ? 'border-emerald-500' : 'border-slate-600'"
              @focus="useCustom = true; quantity = 0"
              @input="useCustom = true"
            />
          </div>
        </div>

        <!-- Cost preview -->
        <div class="mb-4 text-sm text-slate-400">
          <span v-if="effectiveQuantity > 0">
            {{ t('credits.page.cost_preview', { quantity: effectiveQuantity.toLocaleString(), price: costPreview }) }}
          </span>
          <span v-else class="text-slate-600">{{ t('credits.page.cost_preview_empty') }}</span>
        </div>

        <!-- Authoritative amount + Stripe payment element mount point -->
        <div v-if="stripeReady || stripeElements" class="mb-4">
          <div class="mb-3 flex items-baseline justify-between rounded-lg bg-slate-900/60 border border-slate-700 px-4 py-3">
            <span class="text-sm text-slate-400">{{ t('credits.page.amount_to_be_charged') }}</span>
            <span class="text-lg font-semibold text-white">{{ confirmedAmount }}</span>
          </div>
          <p v-if="priceChangedSincePreview" class="mb-3 text-sm text-amber-400">
            {{ t('credits.page.price_updated') }}
          </p>
          <div
            id="credit-payment-element"
            class="rounded-lg overflow-hidden"
          />
          <p v-if="paymentError" class="mt-2 text-sm text-red-400">{{ paymentError }}</p>
        </div>

        <div v-if="paymentError && !stripeElements" class="mb-4 text-sm text-red-400">
          {{ paymentError }}
        </div>

        <!-- Action button -->
        <div class="flex items-center gap-3">
          <button
            v-if="!stripeElements"
            type="button"
            :disabled="purchasing || effectiveQuantity < 1"
            class="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-6 py-2.5 text-sm transition-colors flex items-center gap-2"
            @click="handleBuyNow"
          >
            <AppSpinner v-if="purchasing" size="button" />
            {{ purchasing ? t('credits.page.preparing') : t('credits.page.buy_now_button') }}
          </button>
          <button
            v-else
            type="button"
            :disabled="!stripeReady || purchasing"
            class="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-6 py-2.5 text-sm transition-colors flex items-center gap-2"
            @click="handleConfirmPayment"
          >
            <AppSpinner v-if="purchasing" size="button" />
            {{ purchasing ? t('credits.page.processing') : t('credits.page.pay_button', { price: confirmedAmount }) }}
          </button>
          <button
            v-if="stripeElements"
            type="button"
            class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white text-sm rounded-lg px-4 py-2.5 transition-colors"
            @click="stripeElements = null; stripeInstance = null; stripeReady = false; paymentError = null; confirmedAmountCents = null"
          >
            {{ t('credits.page.cancel_payment_button') }}
          </button>
        </div>

        <p class="mt-3 text-xs text-slate-500">
          {{ t('credits.page.pricing_note') }}
        </p>
      </div>

      <!-- Credit history ledger -->
      <div>
        <h2 class="text-base font-semibold text-white mb-4">{{ t('credits.page.history_heading') }}</h2>

        <div v-if="ledgerError" class="mb-4 rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400">
          {{ ledgerError }}
        </div>

        <CreditLedgerTable
          :entries="entries"
          :loading="ledgerLoading"
          :meta="meta"
          :page="page"
          :per-page="perPage"
          @update:page="onPageChange"
          @update:per-page="onPerPageChange"
        />
      </div>
    </main>
  </OrganizerLayout>
</template>
